import { replayShadowBackend } from './replay';
export var ShadowSyncElementType;
(function (ShadowSyncElementType) {
    ShadowSyncElementType[ShadowSyncElementType["Fragment"] = 0] = "Fragment";
    ShadowSyncElementType[ShadowSyncElementType["Element"] = 1] = "Element";
    ShadowSyncElementType[ShadowSyncElementType["TextNode"] = 2] = "TextNode";
    ShadowSyncElementType[ShadowSyncElementType["Component"] = 3] = "Component";
    ShadowSyncElementType[ShadowSyncElementType["ShadowRoot"] = 4] = "ShadowRoot";
    ShadowSyncElementType[ShadowSyncElementType["VirtualNode"] = 5] = "VirtualNode";
})(ShadowSyncElementType || (ShadowSyncElementType = {}));
export class ShadowSyncElement {
    /** @internal */
    static _prepareElement(context, elem, logicalName, stylingName, ownerShadowRoot) {
        context.channel.createElement(elem._id, logicalName, stylingName, ownerShadowRoot._id);
        return elem;
    }
    static createElement(context, logicalName, stylingName, ownerShadowRoot) {
        return ShadowSyncElement._prepareElement(context, new ShadowSyncElement(context, 1 /* ShadowSyncElementType.Element */, ownerShadowRoot), logicalName, stylingName, ownerShadowRoot);
    }
    /** @internal */
    static _prepareComponent(context, componentElement, shadowRoot, tagName, virtualHost, styleScope, extraStyleScope, externalClasses, slotMode, writeIdToDOM, ownerShadowRoot) {
        context._checkStyleScope(styleScope);
        context._checkStyleScope(extraStyleScope);
        componentElement.shadowRoot = shadowRoot;
        context.channel.createComponent(componentElement._id, shadowRoot._id, tagName, virtualHost, styleScope, extraStyleScope, externalClasses, slotMode, writeIdToDOM, ownerShadowRoot._id);
        return componentElement;
    }
    static createComponent(context, tagName, virtualHost, styleScope, extraStyleScope, externalClasses, slotMode, writeIdToDOM, ownerShadowRoot) {
        return ShadowSyncElement._prepareComponent(context, new ShadowSyncElement(context, 3 /* ShadowSyncElementType.Component */, ownerShadowRoot), new ShadowSyncShadowRoot(context), tagName, virtualHost, styleScope, extraStyleScope, externalClasses, slotMode, writeIdToDOM, ownerShadowRoot);
    }
    /** @internal */
    static _prepareTextNode(context, elem, textContent, ownerShadowRoot) {
        context.channel.createTextNode(elem._id, textContent, ownerShadowRoot._id);
        return elem;
    }
    static createTextNode(context, textContent, ownerShadowRoot) {
        return ShadowSyncElement._prepareTextNode(context, new ShadowSyncElement(context, 2 /* ShadowSyncElementType.TextNode */, ownerShadowRoot), textContent, ownerShadowRoot);
    }
    /** @internal */
    static _prepareVirtualNode(context, elem, virtualName, ownerShadowRoot) {
        context.channel.createVirtualNode(elem._id, virtualName, ownerShadowRoot._id);
        return elem;
    }
    static createVirtualNode(context, virtualName, ownerShadowRoot) {
        return ShadowSyncElement._prepareVirtualNode(context, new ShadowSyncElement(context, 5 /* ShadowSyncElementType.VirtualNode */, ownerShadowRoot), virtualName, ownerShadowRoot);
    }
    static createFragment(context) {
        const elem = new ShadowSyncElement(context, 0 /* ShadowSyncElementType.Fragment */, context.getRootNode());
        context.channel.createFragment(elem._id);
        return elem;
    }
    constructor(_context, type, ownerShadowRoot) {
        this._context = _context;
        this.type = type;
        this.ownerShadowRoot = ownerShadowRoot;
        const id = (this._id = _context._genElementId());
        _context._setElementId(id, this);
    }
    getChannel() {
        return this.ownerShadowRoot.getChannel();
    }
    release() {
        this._context._removeElementId(this._id);
        this.getChannel().release(this._id);
    }
    associateValue(v) {
        this.getChannel().associateValue(this._id, this._context.getAssociateValueInfo(v));
    }
    getShadowRoot() {
        return this.shadowRoot;
    }
    appendChild(child) {
        this.getChannel().appendChild(this._id, child._id);
    }
    removeChild(child) {
        this.getChannel().removeChild(this._id, child._id);
    }
    insertBefore(child, before) {
        this.getChannel().insertBefore(this._id, child._id, before._id);
    }
    replaceChild(child, oldChild) {
        this.getChannel().replaceChild(this._id, child._id, oldChild._id);
    }
    spliceBefore(before, deleteCount, list) {
        this.getChannel().spliceBefore(this._id, before._id, deleteCount, list._id);
    }
    spliceAppend(list) {
        this.getChannel().spliceAppend(this._id, list._id);
    }
    spliceRemove(start, deleteCount) {
        this.getChannel().spliceRemove(this._id, start._id, deleteCount);
    }
    setId(id) {
        this.getChannel().setId(this._id, id);
    }
    setSlot(name) {
        this.getChannel().setSlot(this._id, name);
    }
    setSlotName(name) {
        this.getChannel().setSlotName(this._id, name);
    }
    setSlotElement(slot) {
        var _a, _b;
        this.getChannel().setSlotElement(this._id, (_b = (_a = slot === null || slot === void 0 ? void 0 : slot._slotId) !== null && _a !== void 0 ? _a : slot === null || slot === void 0 ? void 0 : slot._id) !== null && _b !== void 0 ? _b : null);
    }
    setInheritSlots() {
        this.getChannel().setInheritSlots(this._id);
    }
    setStyle(styleText, styleSegmentIndex) {
        this.getChannel().setStyle(this._id, styleText, styleSegmentIndex);
    }
    addClass(className) {
        this.getChannel().addClass(this._id, className);
    }
    removeClass(className) {
        this.getChannel().removeClass(this._id, className);
    }
    setClassAlias(className, target) {
        this.getChannel().setClassAlias(this._id, className, target);
    }
    setAttribute(name, value) {
        this.getChannel().setAttribute(this._id, name, value);
    }
    removeAttribute(name) {
        this.getChannel().removeAttribute(this._id, name);
    }
    setDataset(name, value) {
        this.getChannel().setDataset(this._id, name, value);
    }
    setText(content) {
        this.getChannel().setText(this._id, content);
    }
    getAllComputedStyles(cb) {
        this.getChannel().getAllComputedStyles(this._id, cb);
    }
    getPseudoComputedStyles(pseudoType, cb) {
        this.getChannel().getPseudoComputedStyles(this._id, pseudoType, cb);
    }
    getInheritedRules(cb) {
        this.getChannel().getInheritedRules(this._id, cb);
    }
    getBoundingClientRect(cb) {
        this.getChannel().getBoundingClientRect(this._id, cb);
    }
    getBoxModel(cb) {
        this.getChannel().getBoxModel(this._id, cb);
    }
    getMatchedRules(cb) {
        this.getChannel().getMatchedRules(this._id, cb);
    }
    getScrollOffset(cb) {
        this.getChannel().getScrollOffset(this._id, cb);
    }
    setScrollPosition(scrollLeft, scrollTop, duration) {
        this.getChannel().setScrollPosition(this._id, scrollLeft, scrollTop, duration);
    }
    getPseudoTypes(cb) {
        this.getChannel().getPseudoTypes(this._id, cb);
    }
    setModelBindingStat(attributeName, listener) {
        this.getChannel().setModelBindingStat(this._id, attributeName, listener);
    }
    setListenerStats(type, capture, mutLevel) {
        this.getChannel().setListenerStats(this._id, type, capture, mutLevel);
    }
    createIntersectionObserver(relativeElement, relativeElementMargin, thresholds, listener) {
        const id = this.getChannel().createIntersectionObserver(this._id, relativeElement ? relativeElement._id : null, relativeElementMargin, thresholds, listener);
        return {
            disconnect: () => {
                this.getChannel().disconnectObserver(id);
            },
        };
    }
    createResizeObserver(mode, listener) {
        const id = this.getChannel().createResizeObserver(this._id, mode, listener);
        return {
            disconnect: () => {
                this.getChannel().disconnectObserver(id);
            },
        };
    }
    triggerNativeEvent(type, detail) {
        this.getChannel().triggerNativeEvent(this._id, type, detail);
    }
    manipulateNativeNode(action, args, cb) {
        this.getChannel().manipulateNativeNode(this._id, action, args, cb);
    }
    getContext(cb) {
        this.getChannel().getContext(this._id, cb);
    }
    sendCustomMethod(options) {
        this.getChannel().callCustomMethod(this._id, options);
    }
}
export class ShadowSyncShadowRoot extends ShadowSyncElement {
    getChannel() {
        return this.channel;
    }
    disableAttachment() {
        const noop = () => { };
        this.channel = Object.assign(Object.assign({}, this.channel), { appendChild: noop, removeChild: noop, insertBefore: noop, replaceChild: noop, spliceBefore: noop, spliceAppend: noop, spliceRemove: noop });
    }
    /** @internal */
    constructor(context) {
        super(context, 4 /* ShadowSyncElementType.ShadowRoot */, null);
        this.channel = this._context.channel;
        this.ownerShadowRoot = this;
    }
    createElement(logicalName, stylingName) {
        return ShadowSyncElement.createElement(this._context, logicalName, stylingName, this);
    }
    createTextNode(content) {
        return ShadowSyncElement.createTextNode(this._context, content, this);
    }
    createComponent(tagName, external, virtualHost, styleScope, extraStyleScope, externalClasses, slotMode, writeIdToDOM) {
        return ShadowSyncElement.createComponent(this._context, tagName, virtualHost, styleScope, extraStyleScope, externalClasses, slotMode, writeIdToDOM, this);
    }
    createVirtualNode(virtualName) {
        return ShadowSyncElement.createVirtualNode(this._context, virtualName, this);
    }
}
export class ShadowSyncBackendContext {
    constructor(glassEasel, channel, styleScopeManager, idGenerator) {
        this.glassEasel = glassEasel;
        this.channel = channel;
        this.styleScopeManager = styleScopeManager;
        this.mode = 1;
        this.dropBackendAfterRelease = true;
        this._elementIdMap = [];
        this._slotIdMap = [];
        this._$registeredStyleSheets = [];
        this._$appendedStyleSheets = [];
        this._$styleSheetIdInc = 1;
        this._$styleScopeMap = [''];
        this._windowInfo = {
            width: 0,
            height: 0,
            devicePixelRatio: 0,
        };
        this._themeInfo = {
            theme: 'light',
        };
        this._createEvent = null;
        this._triggerEvent = null;
        this._elementIdGen = idGenerator();
        this._traceIdGen = idGenerator();
        this._shadowRoot = new ShadowSyncShadowRoot(this);
        this.initContext();
    }
    initContext() {
        const { channel } = this;
        channel.create(({ windowInfo, themeInfo }) => {
            this._windowInfo = windowInfo;
            this._themeInfo = themeInfo;
        });
        channel.onEvent((type, detail, options) => this._createEvent(type, detail, options), (event, currentTargetId, mark, targetId, isCapture) => {
            var _a, _b;
            const currentTarget = (_a = this._elementIdMap[currentTargetId]) === null || _a === void 0 ? void 0 : _a.__wxElement;
            const target = (_b = this._elementIdMap[targetId]) === null || _b === void 0 ? void 0 : _b.__wxElement;
            if (!currentTarget || !target)
                return;
            this._triggerEvent(event, currentTarget, mark, target, isCapture);
        });
        channel.onWindowResize((windowInfo) => {
            var _a;
            this._windowInfo = windowInfo;
            (_a = this._windowResizeListener) === null || _a === void 0 ? void 0 : _a.call(this, windowInfo);
        });
        channel.onThemeChange((themeInfo) => {
            var _a;
            this._themeInfo = themeInfo;
            (_a = this._themeChangeListener) === null || _a === void 0 ? void 0 : _a.call(this, themeInfo);
        });
        channel.setDynamicSlotHandler((elementId, slots) => {
            var _a;
            const element = (_a = this._elementIdMap[elementId]) === null || _a === void 0 ? void 0 : _a.__wxElement;
            const comp = element.asGeneralComponent();
            if (!comp || !ReflectTemplateEngine.isHookedReflectingComponent(comp))
                return;
            const shadowRoot = comp.shadowRoot;
            if (shadowRoot.getSlotMode() !== 3 /* this.glassEasel.SlotMode.Dynamic */)
                return;
            const slotElements = [];
            slots.forEach(({ slotId, name, slotValues }) => {
                const elem = shadowRoot.createVirtualNode('slot');
                elem.destroyBackendElementOnRemoval();
                this.glassEasel.Element.setSlotName(elem, name);
                Object.keys(slotValues).forEach((key) => {
                    shadowRoot.replaceSlotValue(elem, key, slotValues[key]);
                });
                shadowRoot.applySlotValueUpdates(elem);
                slotElements.push(elem);
                this._slotIdMap[slotId] = elem;
                elem.$$._slotId = slotId;
            });
            shadowRoot.insertChildren(slotElements, -1);
        }, (slotIds) => {
            slotIds.forEach((slotId) => {
                const elem = this._slotIdMap[slotId];
                if (elem) {
                    const shadowRoot = elem.parentNode;
                    shadowRoot.removeChild(elem);
                    this._slotIdMap[slotId] = undefined;
                }
            });
        }, (slotId, slotValues, changedNames) => {
            const elem = this._slotIdMap[slotId];
            if (elem) {
                const shadowRoot = elem.parentNode;
                changedNames.forEach((key) => {
                    shadowRoot.replaceSlotValue(elem, key, slotValues[key]);
                });
                shadowRoot.applySlotValueUpdates(elem);
            }
        });
    }
    getAssociateValueInfo(node) {
        return {
            isReflect: node.asGeneralComponent() &&
                ReflectTemplateEngine.isHookedReflectingComponent(node.asGeneralComponent()),
        };
    }
    destroy() {
        this.channel.destroy();
    }
    getWindowWidth() {
        return this._windowInfo.width;
    }
    getWindowHeight() {
        return this._windowInfo.height;
    }
    getDevicePixelRatio() {
        return this._windowInfo.devicePixelRatio;
    }
    onWindowResize(cb) {
        this._windowResizeListener = cb;
    }
    getTheme() {
        return this._themeInfo.theme;
    }
    onThemeChange(cb) {
        this._themeChangeListener = cb;
    }
    registerStyleSheetContent(path, content) {
        this._$registeredStyleSheets.push([path, content]);
        this.channel.registerStyleSheetContent(path, content);
    }
    _checkStyleScope(styleScope) {
        if (styleScope !== null && styleScope !== undefined && !this._$styleScopeMap[styleScope]) {
            const stylePrefix = this.styleScopeManager.queryName(styleScope);
            this._$styleScopeMap[styleScope] = stylePrefix;
            this.channel.registerStyleScope(styleScope, stylePrefix);
        }
    }
    appendStyleSheetPath(path, styleScope) {
        this._checkStyleScope(styleScope);
        const id = this._$styleSheetIdInc;
        this._$styleSheetIdInc += 1;
        this._$appendedStyleSheets.push([id, path, styleScope]);
        this.channel.appendStyleSheetPath(id, path, styleScope);
        return id;
    }
    disableStyleSheet(index) {
        this.channel.disableStyleSheet(index);
    }
    replaceStyleSheetAllProperties(sheetIndex, ruleIndex, inlineStyle, callback) {
        this.channel.replaceStyleSheetAllProperties(sheetIndex, ruleIndex, inlineStyle, callback);
    }
    startOverlayInspect(cb) {
        this.channel.startOverlayInspect((event, elementId) => {
            cb(event, elementId ? this._elementIdMap[elementId].__wxElement : null);
        });
    }
    stopOverlayInspect() {
        this.channel.stopOverlayInspect();
    }
    render(cb) {
        this.channel.render(cb);
    }
    getRootNode() {
        return this._shadowRoot;
    }
    createFragment() {
        return ShadowSyncElement.createFragment(this);
    }
    onEvent(_createEvent, _listener) {
        this._createEvent = _createEvent;
        this._triggerEvent = _listener;
    }
    createMediaQueryObserver(status, listener) {
        const id = this.channel.createMediaQueryObserver(status, listener);
        return {
            disconnect: () => {
                this.channel.disconnectObserver(id);
            },
        };
    }
    getActiveElement(cb) {
        this.channel.getActiveElement((elementId) => {
            cb(elementId ? this._elementIdMap[elementId].__wxElement : null);
        });
    }
    elementFromPoint(left, top, cb) {
        this.channel.elementFromPoint(left, top, (elementId) => {
            cb(elementId ? this._elementIdMap[elementId].__wxElement : null);
        });
    }
    _genElementId() {
        return this._elementIdGen.gen();
    }
    _setElementId(id, elem) {
        this._elementIdMap[id] = elem;
    }
    _getElementId(id) {
        return this._elementIdMap[id];
    }
    _removeElementId(id) {
        this._elementIdGen.release(id);
        this._elementIdMap[id] = undefined;
    }
    performanceTraceStart() {
        const id = this._traceIdGen.gen();
        this.channel.performanceStartTrace(id);
        return id;
    }
    performanceTraceEnd(id, cb) {
        this._traceIdGen.release(id);
        this.channel.performanceEndTrace(id, cb);
    }
    reInitContext() {
        this.initContext();
        this._$registeredStyleSheets.forEach(([path, content]) => {
            this.channel.registerStyleSheetContent(path, content);
        });
        this._$styleScopeMap.forEach((stylePrefix, styleScope) => {
            this.channel.registerStyleScope(styleScope, stylePrefix);
        });
        this._$appendedStyleSheets.forEach(([id, path, styleScope]) => {
            this.channel.appendStyleSheetPath(id, path, styleScope);
        });
    }
    replay(glassEasel, roots, getShadowSyncElement, afterReplayElement) {
        roots.forEach((root) => {
            replayShadowBackend(glassEasel, this, root, {
                createElement: (ownerShadowRoot, elem) => {
                    var _a, _b;
                    let be;
                    if (glassEasel.TextNode.isTextNode(elem)) {
                        be = ShadowSyncElement._prepareTextNode(this, getShadowSyncElement(elem), elem.textContent, ownerShadowRoot);
                    }
                    else if (glassEasel.NativeNode.isNativeNode(elem)) {
                        be = ShadowSyncElement._prepareElement(this, getShadowSyncElement(elem), elem.is, elem.stylingName, ownerShadowRoot);
                    }
                    else if (glassEasel.VirtualNode.isVirtualNode(elem)) {
                        // shadowRoot is created by createComponent, ignore it
                        be = ShadowSyncElement._prepareVirtualNode(this, getShadowSyncElement(elem), elem.is, ownerShadowRoot);
                    }
                    else if (glassEasel.Component.isComponent(elem)) {
                        const options = elem.getComponentOptions();
                        const [styleScope, extraStyleScope] = elem.getStyleScopes();
                        be = ShadowSyncElement._prepareComponent(this, getShadowSyncElement(elem), getShadowSyncElement(elem.getShadowRoot()), elem.tagName, elem.isVirtual(), styleScope !== null && styleScope !== void 0 ? styleScope : glassEasel.StyleScopeManager.globalScope(), extraStyleScope, Object.keys(elem.getExternalClasses()), (_b = (_a = elem.getShadowRoot()) === null || _a === void 0 ? void 0 : _a.getSlotMode()) !== null && _b !== void 0 ? _b : null, options.writeIdToDOM, ownerShadowRoot);
                    }
                    else {
                        throw new Error(`Unknown elem type ${elem.constructor.name}`);
                    }
                    return be;
                },
                afterReplayElement,
            });
            this._shadowRoot.appendChild(getShadowSyncElement(root));
        });
    }
    sendCustomMethod(options) {
        this.channel.callCustomMethod(null, options);
    }
    onCustomMethodFromView(handler) {
        this.channel.onCustenMethod((elementId, options) => {
            const element = elementId !== null ? this._getElementId(elementId).__wxElement : null;
            handler(element, options);
        });
    }
}
export class ReflectTemplateEngine {
    static isHookedReflectingComponent(component) {
        return component.getComponentOptions().templateEngine === ReflectTemplateEngine;
    }
    static create(_behavior, _componentOptions) {
        return new ReflectTemplateEngine();
    }
    createInstance(elem, createShadowRoot) {
        const context = elem.getBackendContext();
        if (!(context instanceof ShadowSyncBackendContext)) {
            throw new Error('backendContext is not ShadowSyncBackendContext');
        }
        const { channel } = context;
        const backendElement = elem.getBackendElement();
        const shadowRoot = createShadowRoot(elem);
        const backendShadowRoot = shadowRoot.$$;
        // prevent childNodes from attaching to shadowRoot
        backendShadowRoot.disableAttachment();
        const instance = {
            shadowRoot,
            initValues: (data) => {
                channel.initValues(backendElement._id, data);
            },
            updateValues: (data, changes) => {
                channel.updateValues(backendElement._id, changes);
            },
        };
        return instance;
    }
}
export const hookBuilderToSyncData = (builder, getComponentFromMethodCaller) => {
    const properties = [];
    const getContextFromMethodCaller = (methodCaller) => {
        const component = (getComponentFromMethodCaller === null || getComponentFromMethodCaller === void 0 ? void 0 : getComponentFromMethodCaller(methodCaller)) || methodCaller;
        const context = component.getBackendContext();
        const backendElement = component.getBackendElement();
        if (!(context instanceof ShadowSyncBackendContext)) {
            throw new Error('');
        }
        return { component, context, backendElement };
    };
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const builderPrototype = Object.getPrototypeOf(builder);
    let pendingChanges;
    const addObserver = (builder, name) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        builderPrototype.observer.call(builder, name, function (newValue) {
            const { context, backendElement } = getContextFromMethodCaller(this);
            if (!pendingChanges) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises, promise/catch-or-return, promise/always-return
                Promise.resolve().then(() => {
                    context.channel.updateValues(backendElement._id, pendingChanges);
                    pendingChanges = undefined;
                });
            }
            pendingChanges = pendingChanges || [];
            pendingChanges.push([[name], newValue, undefined, undefined]);
        });
    };
    Object.setPrototypeOf(builder, Object.create(builderPrototype, {
        property: {
            value(name, def) {
                properties.push(name);
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                builderPrototype.property.call(this, name, def);
                addObserver(this, name);
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return this;
            },
        },
        staticData: {
            value(data) {
                const keys = Object.keys(data || {});
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                builderPrototype.staticData.call(this, data);
                keys.forEach((key) => {
                    properties.push(key);
                    addObserver(this, key);
                });
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return this;
            },
        },
    }));
    return builder.init(({ self, lifetime }) => {
        lifetime('created', () => {
            const { component, context, backendElement } = getContextFromMethodCaller(self);
            context.channel.initValues(backendElement._id, properties.reduce((initValues, property) => {
                initValues[property] = component.data[property];
                return initValues;
            }, {}));
        });
    });
};
export const getNodeId = (node) => node.getBackendElement()._id;
export { ChannelEventType, MessageChannelDataSide } from './message_channel';
export { replayShadowBackend } from './replay';
export { getLinearIdGenerator, getRandomIdGenerator } from './utils';
//# sourceMappingURL=backend.js.map